/**
 * GDI Inline Shortcode Parser
 *
 * Parses CMS-authored shortcode directives embedded in HTML text and converts
 * them to inline HTML markup. Shortcodes use a JSON-like object syntax:
 *
 *   {directive: value, text: 'your inline text'}
 *
 * The directive registry (DIRECTIVE_RENDERERS) and icon sprite map (ICON_SPRITE_MAP)
 * are exported so they can be extended or overridden in consuming code.
 *
 * Colors are resolved via Chakra CSS variables. Use any token path from the
 * design system (e.g. webTypo.textPrimary, webBasic.50). Dot notation is
 * converted to Chakra's dash convention automatically. Explicit hex, rgb, or
 * hsl values are passed through unchanged.
 *
 * Icons are resolved from ICON_SPRITE_MAP (design-system component names →
 * SVG sprite IDs). Use the component name as defined on the Icons design-system
 * page (e.g. Account, MiniCartIcon, HeartIcon).
 *
 * Tooltips output a <span data-gdi-tooltip="..."> marker that the consuming
 * React component (GDITextBlock) hydrates into a design-system Tooltip via
 * a React portal, so no custom CSS is required.
 */

import {decodeHtmlEntities} from './gdi-html-entities'

// ─── Brace scanner ────────────────────────────────────────────────────────────

/**
 * Determine whether the quote character at index `i` closes an open quoted
 * string. CMS-authored copy frequently contains literal apostrophes inside
 * single-quoted values (e.g. `text: 'centre d'expédition ici'`). A quote only
 * closes the string when it is immediately followed by a value terminator
 * (whitespace, `,`, `}`, `]`, `|`) or the end of input; otherwise it is treated
 * as a literal character within the string.
 */
const closesQuotedString = (str, i) => {
    const next = str[i + 1]
    return next === undefined || /[\s,}\]|]/.test(next)
}

/**
 * Starting from the `{` at index `start`, return the index of the matching `}`.
 * Respects nested braces and single/double-quoted strings.
 * Returns -1 if no closing brace is found.
 */
const findClosingBrace = (str, start) => {
    let depth = 0
    let inSingle = false
    let inDouble = false

    for (let i = start; i < str.length; i++) {
        const ch = str[i]
        const escaped = i > 0 && str[i - 1] === '\\'

        if (ch === "'" && !inDouble && !escaped) {
            if (!inSingle) inSingle = true
            else if (closesQuotedString(str, i)) inSingle = false
        } else if (ch === '"' && !inSingle && !escaped) {
            if (!inDouble) inDouble = true
            else if (closesQuotedString(str, i)) inDouble = false
        } else if (!inSingle && !inDouble) {
            if (ch === '{') depth++
            else if (ch === '}') {
                if (--depth === 0) return i
            }
        }
    }
    return -1
}

// ─── Shortcode extractor ──────────────────────────────────────────────────────

/**
 * Find all top-level shortcode blocks in an HTML string.
 * Only blocks that start with a letter after the opening brace are considered
 * (to avoid false matches on CSS or template expressions).
 *
 * @param {string} html
 * @returns {{ raw: string, start: number, end: number }[]}
 */
export const extractShortcodes = (html) => {
    const results = []
    let i = 0

    while (i < html.length) {
        const idx = html.indexOf('{', i)
        if (idx === -1) break

        // Only treat as a shortcode if next char is a letter (directive name starts)
        if (!/[a-zA-Z]/.test(html[idx + 1] ?? '')) {
            i = idx + 1
            continue
        }

        const end = findClosingBrace(html, idx)
        if (end === -1) {
            i = idx + 1
            continue
        }

        results.push({raw: html.slice(idx, end + 1), start: idx, end})
        i = end + 1
    }

    return results
}

// ─── Parser ───────────────────────────────────────────────────────────────────

/**
 * Parse a single value starting at index `i` in `src`.
 * Handles: single-quoted strings, double-quoted strings, nested objects,
 * booleans, numbers, and unquoted identifiers.
 *
 * @returns {{ value: *, next: number }}
 */
const parseValue = (src, i) => {
    while (i < src.length && src[i] === ' ') i++
    const ch = src[i]

    // Quoted string
    if (ch === "'" || ch === '"') {
        const q = ch
        let j = i + 1
        while (j < src.length) {
            if (src[j] === '\\') {
                j += 2
                continue
            }
            if (src[j] === q && closesQuotedString(src, j)) break
            j++
        }
        const value = src
            .slice(i + 1, j)
            .replace(/\\'/g, "'")
            .replace(/\\"/g, '"')
            .replace(/\\\|/g, '|')
            .replace(/\\\[/g, '[')
            .replace(/\\\]/g, ']')
        return {value, next: j + 1}
    }

    // Nested object
    if (ch === '{') {
        const end = findClosingBrace(src, i)
        if (end !== -1) {
            return {value: parseObject(src.slice(i + 1, end).trim()), next: end + 1}
        }
    }

    // Unquoted identifier / keyword / hex color / number
    let j = i
    while (j < src.length && src[j] !== ',' && src[j] !== '}') j++
    const raw = src.slice(i, j).trim()

    if (raw === 'true') return {value: true, next: j}
    if (raw === 'false') return {value: false, next: j}
    if (raw !== '' && !isNaN(raw)) return {value: Number(raw), next: j}
    return {value: raw, next: j}
}

/**
 * Parse a comma-separated list of key:value pairs from `src` into a plain object.
 *
 * @param {string} src - Inner content of a shortcode (without outer braces)
 * @returns {Object}
 */
const parseObject = (src) => {
    const obj = {}
    let i = 0
    const len = src.length

    while (i < len) {
        while (i < len && /\s/.test(src[i])) i++
        if (i >= len) break

        // Parse key (unquoted identifier)
        const ks = i
        while (i < len && /[a-zA-Z0-9_.-]/.test(src[i])) i++
        const key = src.slice(ks, i).trim()
        if (!key) break

        // Skip whitespace + colon
        while (i < len && /[\s:]/.test(src[i])) i++

        const {value, next} = parseValue(src, i)
        obj[key] = value
        i = next

        while (i < len && /[\s,]/.test(src[i])) i++
    }

    return obj
}

/**
 * Parse a raw shortcode string (including outer braces) into a plain object.
 *
 * @param {string} raw - e.g. "{fontStyle: bold, text: 'hello'}"
 * @returns {Object}
 */
export const parseShortcode = (raw) => parseObject(raw.slice(1, -1).trim())

// ─── HTML helpers ─────────────────────────────────────────────────────────────

const escapeHtml = (str) =>
    String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

const escapeAttr = (str) =>
    String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

/**
 * Line breaks are the one piece of "HTML" CMS authors legitimately need
 * inside a shortcode's `text` value — either a literal `<br>`/`<br/>`/`<br />`
 * tag or a raw newline typed in a CMS textarea. Everything else in `text`
 * must stay escaped (XSS protection), so line breaks are swapped for a
 * placeholder before escaping and restored as a real `<br />` afterwards,
 * instead of allowing HTML through wholesale.
 */
const BR_TAG_RE = /<br\s*\/?>/gi
const BR_PLACEHOLDER = ' gdi-br '

const escapeHtmlPreservingLineBreaks = (str) =>
    escapeHtml(
        String(str).replace(BR_TAG_RE, BR_PLACEHOLDER).replace(/\r\n|\r|\n/g, BR_PLACEHOLDER)
    ).replaceAll(BR_PLACEHOLDER, '<br />')

// ─── Color resolver ──────────────────────────────────────────────────────────

/**
 * Resolve a color value to a CSS string.
 *
 * - Explicit values (#hex, rgb(…), hsl(…)) are returned as-is.
 * - Design-system token paths use Chakra's nested-object convention, e.g.
 *     webTypo.textPrimary  →  var(--chakra-colors-webTypo-textPrimary)
 *     webBasic.50          →  var(--chakra-colors-webBasic-50)
 *   Dot separators are converted to dashes automatically.
 */
const resolveColor = (value) => {
    if (!value) return null
    const v = String(value)
    if (/^#|^rgb|^hsl/.test(v)) return v
    return `var(--chakra-colors-${v.replace(/\./g, '-')})`
}

// ─── fontStyle maps ───────────────────────────────────────────────────────────

/**
 * fontStyle values that map to semantic HTML tags.
 * The function receives the escaped text and returns the wrapped HTML.
 */
const FONT_STYLE_TAG_MAP = {
    bold: (t) => `<strong>${t}</strong>`,
    italic: (t) => `<em>${t}</em>`,
    'bold-italic': (t) => `<strong><em>${t}</em></strong>`,
    subscript: (t) => `<sub>${t}</sub>`,
    superscript: (t) => `<sup>${t}</sup>`,
    strikethrough: (t) => `<s>${t}</s>`
}

/**
 * fontStyle values that map to inline CSS on a `<span>`.
 * Extend this map to add new font style variants.
 */
export const FONT_STYLE_CSS_MAP = {
    condensed: 'font-stretch:condensed',
    light: 'font-weight:300',
    medium: 'font-weight:500',
    semibold: 'font-weight:600',
    underline: 'text-decoration:underline',
    uppercase: 'text-transform:uppercase',
    capitalize: 'text-transform:capitalize',
    'small-caps': 'font-variant:small-caps',
    monospace: 'font-family:monospace,monospace',
    unset:
        'font-weight:normal;font-style:normal;text-decoration:none;' +
        'text-transform:none;font-variant:normal;font-stretch:normal'
}

/**
 * CSS equivalents for the tag-based fontStyle values. Used when fontStyle is
 * a space-separated multi-token value (e.g. "unset medium italic") — every
 * token is resolved to CSS so the order of declarations determines the final
 * computed style (later declarations override earlier ones, e.g. `unset` first
 * resets all typography, then `medium italic` re-applies the desired weight
 * and style on top).
 */
const FONT_STYLE_TAG_CSS_MAP = {
    bold: 'font-weight:bold',
    italic: 'font-style:italic',
    'bold-italic': 'font-weight:bold;font-style:italic',
    strikethrough: 'text-decoration:line-through',
    subscript: 'vertical-align:sub;font-size:smaller',
    superscript: 'vertical-align:super;font-size:smaller'
}

// ─── Icon sprite map ─────────────────────────────────────────────────────────

/**
 * Maps design-system icon component names to their SVG sprite IDs.
 *
 * Use a component name from the Icons design-system page (e.g. Account,
 * MiniCartIcon, HeartIcon) as the `icon` value in a shortcode. The renderer
 * looks up the sprite ID here and outputs an <svg><use href="#id"> element.
 *
 * If a name is not in this map the value is used as the sprite ID directly,
 * allowing raw sprite IDs to be used as a fallback.
 */
export const ICON_SPRITE_MAP = {
    Account: 'account-icon',
    MiniCartIcon: 'mini-cart',
    AddIcon: 'quantity-plus',
    MinusIcon: 'quantity-minus',
    GeneralSearchIcon: 'general-search',
    SmartSearchIcon: 'smart-search',
    HeaderMenuIcon: 'header-menu',
    HeartIcon: 'heart',
    HeartSolidIcon: 'heart-solid',
    CheckMarkIcon: 'check-mark',
    InfoCircle: 'info-circle',
    ChevronDownIcon: 'chevron-down',
    ChevronUpIcon: 'chevron-up',
    ChevronLeftIcon: 'chevron-left',
    ChevronRightIcon: 'chevron-right',
    CustomClose: 'custom-close',
    LeftArrow: 'left-arrow',
    EditPencil: 'edit-pencil',
    AlertTriangleIcon: 'alert-triangle-icon',
    ErrorIcon: 'error-icon',
    ConfirmationIcon: 'confirmation-icon',
    LoyaltyIcon: 'loyalty-icon',
    GiftIcon: 'gift-icon',
    OrderIcon: 'order-icon',
    NeedHelpIcon: 'need-help',
    ChatIcon: 'chat',
    Globe: 'globe',
    GlobeDark: 'globe-dark',
    NotifyMeEnvelope: 'notify-me-envelope',
    ReturnIcon: {id: 'return-icon', width: 20, height: 20}
}

// ─── Icon renderer ────────────────────────────────────────────────────────────

/**
 * Default icon renderer.
 *
 * - URL values render as an `<img>` tag.
 * - Named values are looked up in ICON_SPRITE_MAP and rendered as an
 *   `<svg><use href="#sprite-id">` element matching the design-system pattern.
 *   Unknown names fall back to using the value directly as the sprite ID.
 *
 * Override via the `iconRenderer` option in `parseShortcodes()` to supply a
 * fully custom renderer.
 *
 * @param {string} name - Design-system icon component name or URL
 * @returns {string} HTML string
 */
export const defaultIconRenderer = (name, {size, margin, align} = {}) => {
    // Normalise size: a bare number (e.g. 24) is treated as px for CSS contexts.
    const resolvedSize = size != null ? String(size) : null
    const cssSizeValue =
        resolvedSize != null
            ? /^\d+(\.\d+)?$/.test(resolvedSize)
                ? `${resolvedSize}px`
                : resolvedSize
            : null
    const verticalAlign = align ?? 'middle'
    const marginStyle = margin != null ? `;margin:${String(margin)}` : ''

    if (/^https?:\/\//.test(name)) {
        const imgStyles = `display:inline-block;vertical-align:${verticalAlign}${
            cssSizeValue ? `;width:${cssSizeValue};height:${cssSizeValue}` : ''
        }${marginStyle}`
        return `<img src="${escapeAttr(name)}" class="gdi-inline-icon" alt="" aria-hidden="true" style="${imgStyles}" />`
    }
    const entry = ICON_SPRITE_MAP[name] ?? name
    const spriteId = typeof entry === 'object' ? entry.id : entry
    const width = resolvedSize ?? (typeof entry === 'object' ? (entry.width ?? 16) : 16)
    const height = resolvedSize ?? (typeof entry === 'object' ? (entry.height ?? 16) : 16)
    return (
        `<svg class="gdi-inline-icon" role="presentation" aria-hidden="true" ` +
        `width="${width}" height="${height}" focusable="false" ` +
        `style="display:inline-block;vertical-align:${verticalAlign}${marginStyle}">` +
        `<use href="#${escapeAttr(spriteId)}"></use>` +
        `</svg>`
    )
}

// ─── Link href resolver ───────────────────────────────────────────────────────

/**
 * Resolve the `link` directive object to an href string.
 * Supports linkType: url | product | category | page
 *
 * Drawer links are intentionally NOT handled here — they are rendered with
 * a `data-gdi-drawer` attribute in `renderShortcode` and handled in
 * `gdiTextBlock` via a React click handler.
 *
 * For `linkType: 'category'`, an optional `categoryUrls` map (categoryId →
 * SCAPI c_relativeUrl, e.g. '/g/clothing/tops/') can be supplied via the
 * second argument. When the categoryId is present in the map, the resolved
 * URL is used; otherwise the parser falls back to `/${brandId|g}/${categoryId}/`
 * so design-system previews and offline parsing still produce a navigable link.
 *
 * Internal paths are passed through the optional `buildUrl` helper so they carry
 * the current site/locale prefix (e.g. `/klarna.html` → `/us/klarna.html`).
 * Absolute URLs and design-system previews (no `buildUrl`) are left untouched.
 *
 * @param {Object} linkObj
 * @param {Object} [opts]
 * @param {Object<string,string>} [opts.categoryUrls] - categoryId → resolved URL
 * @param {function(string): string} [opts.buildUrl] - site/locale URL builder
 * @returns {string|null}
 */
const UNSAFE_HREF_RE = /^\s*javascript:/i

export const resolveLinkHref = (linkObj, {categoryUrls, buildUrl} = {}) => {
    if (!linkObj?.linkType) return null

    const {linkType, href, productId, productName, categoryId, brandId, pageId} = linkObj

    const localize = (path) =>
        typeof buildUrl === 'function' && typeof path === 'string' && path.startsWith('/')
            ? buildUrl(path)
            : path

    switch (linkType) {
        case 'url':
            if (!href || UNSAFE_HREF_RE.test(href)) return null
            return localize(href)
        case 'product':
            if (!productId) return null
            // Matches the PWA Kit product route: /p/:productName/:productId.html
            return localize(
                `/p/${encodeURIComponent(productName || 'product')}/${productId}.html`
            )
        case 'category':
            if (!categoryId) return null
            // Prefer the SSR-resolved SCAPI url (c_relativeUrl) so the link points
            // to the real category path (e.g. /g/clothing/tops/) instead of the
            // raw id (/g/grg_clothing_tops/). Fall back to the id-based path so
            // design-system previews still render a link.
            if (categoryUrls && categoryUrls[categoryId]) {
                return localize(categoryUrls[categoryId])
            }
            // Matches the PWA Kit category route: /:brandId/:categoryPath/
            return localize(`/${brandId || 'g'}/${categoryId}/`)
        case 'page':
            return pageId ? localize(`/${pageId}.html`) : null
        case 'drawer':
            // Drawers are rendered via data-gdi-drawer; href is always null.
            return null
        default:
            return null
    }
}

/**
 * Scan an HTML / text string for `{link: {linkType: 'category', categoryId: 'X'}}`
 * shortcodes and collect the referenced category IDs.
 *
 * Used by the SSR pipeline to discover category IDs embedded in GDITextBlock
 * `TextForWeb` strings so their SCAPI URLs can be resolved up-front, mirroring
 * the existing CategoryLinkBlock / GDILinkBlock resolution.
 *
 * @param {string} html
 * @param {Set<string>} [set] - optional Set to append to (created if omitted)
 * @returns {Set<string>}
 */
export const extractCategoryIdsFromText = (html, set = new Set()) => {
    if (!html || typeof html !== 'string') return set
    const shortcodes = extractShortcodes(html)
    for (const {raw} of shortcodes) {
        try {
            const parsed = parseShortcode(raw)
            const link = parsed?.link
            if (link && link.linkType === 'category' && link.categoryId) {
                set.add(link.categoryId)
            }
        } catch (_) {
            // Ignore unparsable shortcodes — they are left untouched at render time.
        }
    }
    return set
}

// ─── Directive renderer registry ─────────────────────────────────────────────

/**
 * DIRECTIVE_RENDERERS documents which directives exist and how they are handled.
 *
 * All directives are processed together inside `renderShortcode` so that
 * combining works correctly. To add a new directive:
 *   1. Add an entry here (for documentation / discoverability).
 *   2. Handle it in `renderShortcode` below.
 *
 * @type {Object.<string, string>}
 */
export const DIRECTIVE_RENDERERS = {
    brandName:
        'inline — replaced at render-time with the brand name supplied via options.brandName (e.g. "Garage" or "Dynamite"); falls back to [BrandName] when the option is absent (design-system previews)',
    currentYear:
        'inline — replaced at render-time with the current 4-digit year (e.g. 2026); always resolved from new Date().getFullYear() so it never requires a parse option',
    fontStyle:
        'inline — semantic tag or CSS span on text only. Accepts a single ' +
        'token (bold, italic, light, medium, semibold, condensed, underline, ' +
        'uppercase, unset, …) or a space-separated list (e.g. "unset medium ' +
        'italic"). In the multi-token form every token resolves to CSS and ' +
        'later declarations override earlier ones, so authors can prepend ' +
        '`unset` to wipe inherited typography and then layer the desired ' +
        'weight / style on top.',
    fontSize:
        'inline — sets font size on the container span; accepts either a design-system textStyle token ' +
        '(e.g. webH1, bodySmRegular) resolved to full Chakra typography styles via data-gdi-textstyle, ' +
        'or a direct CSS font-size value (e.g. 14px, 1.5rem, 0.875em); bare numbers are treated as px',
    fontColor: 'inline — color style on container span (design-system token or explicit value)',
    highlight: 'inline — background-color style on container span',
    backgroundColor: 'inline — alias for highlight',
    icon: 'inline — SVG sprite icon via ICON_SPRITE_MAP; override with iconRenderer option',
    iconSize:
        'inline — overrides the width and height of the icon; accepts a unitless number (e.g. 24, treated as px) or any CSS length value (e.g. 1.5rem); applies to both SVG and img icons',
    iconMargin:
        'inline — sets CSS margin on the icon element; accepts any valid CSS margin shorthand (e.g. 0 8px 0 0, 4px)',
    iconAlign:
        'inline — vertically aligns the icon within an inline-flex container wrapping icon + text; ' +
        'accepted values: middle (default, maps to center), top, bottom, baseline; ' +
        'CSS flex align-items values (center, flex-start, flex-end) are also accepted',

    link: 'inline — wraps content in <a>; drawer links use data-gdi-drawer; clara-chat links use data-gdi-clara-chat and open the Clara chat widget on click',
    tooltip:
        'inline — standalone; outputs <span data-gdi-tooltip> marker; hydrated to Tooltip by gdiTextBlock',
    readMore:
        'block — standalone; outputs <span data-gdi-readmore="N"> marker; gdiTextBlock clamps content to N lines with animated reveal',
    lineHeight:
        'inline — sets the CSS line-height on the container span; accepts unitless numbers (e.g. 1.5), pixel values (e.g. 20px), or percentages (e.g. 120%)',
    userState:
        'inline — picks registered or unregistered text at render-time based on options.isRegistered; value must be an object {registered: string, unregistered: string}; combines with all other directives (fontStyle, fontColor, link, etc.); falls back to text when options.isRegistered is not supplied (CMS preview)',
    map:
        'standalone — renders an interactive Google Maps iframe (no API key required); ' +
        'coordinate mode: {map, lat: 45.5, lng: -73.5}; place mode: {map: place-name}; ' +
        'optional: zoom (default 13), width (default 100%), height (desktop) and heightMobile; ' +
        'when no height is authored the map keeps a fluid 4/3 aspect ratio, and heightMobile ' +
        'falls back to height when omitted',
    divider:
        'standalone — outputs a <div data-gdi-divider="…JSON props…"> marker that the consuming ' +
        'component (gdiTextBlock and the CMS design-system preview) hydrates into a Chakra ' +
        '<Divider /> via a React portal. Any properties supplied alongside the `divider` flag ' +
        'are forwarded directly to the Divider component (e.g. orientation, variant, ' +
        'borderColor, borderStyle, borderWidth, opacity, my, mx, w, h).',
    rangeMarker:
        'standalone — renders a horizontal range bar with evenly-spaced labelled tick marks and ' +
        'a filled-circle position indicator. ' +
        'labels: pipe-separated list of label strings (e.g. labels: \'Rigid|Comfort|Stretch\'). ' +
        'value: numeric position on the 0→(N-1) scale where N is the number of labels; decimals ' +
        'are supported (e.g. value: 0.7 places the marker 35 % across a 3-label bar). ' +
        'color: optional design-system token (e.g. webTypo.textPrimary) for the track and marker. ' +
        'Standard spacing props (my, mb, mt, etc.) are forwarded to the container.'
}

// ─── Icon align → flex map ──────────────────────────────────────────────────

/**
 * Maps iconAlign shorthand values to CSS flex align-items.
 * CSS vertical-align equivalents are accepted for familiarity;
 * they are mapped to the closest flex value.
 */
const ICON_ALIGN_TO_FLEX = {
    middle: 'center',
    center: 'center',
    top: 'flex-start',
    'flex-start': 'flex-start',
    start: 'flex-start',
    bottom: 'flex-end',
    'flex-end': 'flex-end',
    end: 'flex-end',
    baseline: 'baseline',
    'text-top': 'flex-start',
    'text-bottom': 'flex-end'
}

// ─── Main shortcode renderer ──────────────────────────────────────────────────

/**
 * Render a single parsed shortcode object to an HTML string.
 *
 * Combining rules (per spec):
 *   - `tooltip` is standalone — all other directives are ignored when present.
 *   - `fontStyle` applies to text only, never to the icon.
 *   - `fontColor` and `highlight`/`backgroundColor` apply to the whole container
 *     (icon + text).
 *   - `link` wraps the entire output.
 *
 * @param {Object} parsed         - The parsed shortcode object
 * @param {Object} [options]
 * @param {Function} [options.iconRenderer]  - Custom icon renderer (name → HTML string)
 * @param {Object}   [options.colorTokenMap] - Override or extend COLOR_TOKEN_MAP
 * @returns {string} HTML string
 */
export const renderShortcode = (parsed, options = {}) => {
    const {iconRenderer = defaultIconRenderer} = options
    const {
        text = '',
        icon,
        iconSize,
        iconMargin,
        iconAlign,
        fontStyle,
        fontSize,
        fontColor,
        highlight,
        backgroundColor,
        link,
        tooltip,
        readMore,
        lineHeight
    } = parsed

    // brandName directive: replace text with the runtime brand name from options.
    // Falls back to the shortcode's own `text` value first, then '[BrandName]' so
    // design-system previews (which call parseShortcodes without options) still render.
    // currentYear directive: always replaced with the current 4-digit year.
    // userState directive: picks the registered or unregistered string at render-time
    // based on options.isRegistered. Falls back to `text` when the option is absent
    // so CMS previews (which call parseShortcodes without options) still render.
    const resolvedText =
        parsed.currentYear !== undefined
            ? String(new Date().getFullYear())
            : parsed.brandName !== undefined
            ? options.brandName || text || '[BrandName]'
            : parsed.userState !== undefined
            ? (options.isRegistered
                ? parsed.userState?.registered
                : parsed.userState?.unregistered) ?? text
            : text

    const escapedText = escapeHtmlPreservingLineBreaks(decodeHtmlEntities(resolvedText))

    // userState: when the current auth state has no matching text and there is no
    // icon to render, return empty immediately so no invisible wrapper is emitted.
    if (parsed.userState !== undefined && !escapedText && !icon) return ''

    // tooltip is standalone — output a marker span that gdiTextBlock hydrates into
    // a design-system Tooltip component (Tooltip + IconButton + InfoCircle) via a
    // React portal. This avoids recreating the global tooltip and requires no CSS.
    if (tooltip) {
        const tooltipContent = String(tooltip)
        const attrs = [`data-gdi-tooltip="${escapeAttr(tooltipContent)}"`]
        if (escapedText) attrs.push(`data-gdi-tooltip-label="${escapeAttr(escapedText)}"`)
        return `<span ${attrs.join(' ')}></span>`
    }

    // map is standalone — renders an interactive Google Maps Embed iframe.
    // Requires options.googleMapsApiKey; returns '' silently when absent.
    //
    // Coordinate mode:  {map, lat: 45.5017, lng: -73.5673}
    // Place mode:       {map: 'Garage Montreal'}
    // With options:     {map, lat: 45.5, lng: -73.5, zoom: 14, height: 300px, width: 80%}
    // Responsive:       {map, lat: 45.5, lng: -73.5, height: 600px, heightMobile: 300px}
    if ('map' in parsed) {
        const zoom = parsed.zoom != null ? Math.max(1, Math.min(21, Number(parsed.zoom))) : 13

        const placeQuery =
            typeof parsed.map === 'string' && parsed.map.trim() !== '' ? parsed.map.trim() : null

        if (!placeQuery && (parsed.lat == null || parsed.lng == null)) {
            return ''
        }

        // Emit a marker span that gdiTextBlock hydrates into a GDIInlineMap React
        // component via a portal, enabling the full custom store-locator map theme.
        const mapData = JSON.stringify({
            lat: parsed.lat != null ? Number(parsed.lat) : null,
            lng: parsed.lng != null ? Number(parsed.lng) : null,
            place: placeQuery || null,
            zoom
        })

        // Build container style. Width defaults to 100% (fills container).
        // aspect-ratio 4/3 keeps the map fluid when no height is authored; it is
        // ignored by the browser as soon as an explicit height applies.
        // Heights are emitted as CSS custom properties rather than an inline
        // `height` because inline styles cannot carry a media query — gdiTextBlock
        // picks --gdi-map-h-mobile or --gdi-map-h per breakpoint.
        const toCssLength = (value) => {
            const raw = String(value)
            return /^\d+(\.\d+)?$/.test(raw) ? `${raw}px` : raw
        }

        const parts = ['display:block', 'position:relative', 'overflow:hidden', 'aspect-ratio:4/3']

        // max-width keeps an authored fixed width (e.g. 1080px) from overflowing a
        // narrower column — and from blowing out the layout entirely on mobile.
        parts.push('max-width:100%')

        if (parsed.width != null) {
            parts.push(`width:${escapeAttr(toCssLength(parsed.width))}`)
        } else {
            parts.push('width:100%')
        }

        if (parsed.height != null) {
            parts.push(`--gdi-map-h:${escapeAttr(toCssLength(parsed.height))}`)
        }

        if (parsed.heightMobile != null) {
            parts.push(`--gdi-map-h-mobile:${escapeAttr(toCssLength(parsed.heightMobile))}`)
        }

        return (
            `<span data-gdi-map="${escapeAttr(mapData)}" ` +
            `style="${parts.join(';')}"></span>`
        )
    }

    // readMore is standalone — outputs a hidden marker that gdiTextBlock detects
    // after render to clamp the WHOLE block content to N lines and add an
    // animated reveal button below it.
    //
    // Supports:
    //   {readMore: 3}                                       ← 3 lines, default labels
    //   {readMore: {lines: 3}}                              ← same
    //   {readMore: {lines: 3, moreLabel: 'See more', lessLabel: 'See less'}}
    if (readMore !== undefined && readMore !== null && readMore !== '') {
        const cfg = typeof readMore === 'object' && readMore !== null ? readMore : {lines: readMore}
        const parsedLines = parseInt(cfg.lines ?? 3, 10)
        const lines = Math.max(1, isNaN(parsedLines) ? 3 : parsedLines)
        const moreLabel = String(cfg.moreLabel ?? 'Read More')
        const lessLabel = String(cfg.lessLabel ?? 'Read Less')
        return (
            `<span data-gdi-readmore="${lines}" ` +
            `data-gdi-readmore-more="${escapeAttr(moreLabel)}" ` +
            `data-gdi-readmore-less="${escapeAttr(lessLabel)}" ` +
            `style="display:none"></span>`
        )
    }

    // divider is standalone — outputs a block-level marker that the consuming
    // component (gdiTextBlock and the CMS design-system preview) hydrates into
    // a Chakra <Divider /> via a React portal. All properties supplied
    // alongside the `divider` flag are JSON-serialised onto the marker and
    // forwarded directly to the Divider component (e.g. orientation, variant,
    // borderColor, borderStyle, borderWidth, opacity, my, mx, w, h).
    //
    //   {divider}
    //   {divider, my: '20px'}
    //   {divider, borderColor: 'webBasic.200', borderWidth: '2px'}
    //   {divider, orientation: 'vertical', height: '40px'}
    if ('divider' in parsed) {
        // Strip the discriminator and any unrelated text/icon keys — divider
        // is standalone and ignores text content. Everything else passes
        // through to the Chakra Divider as a prop.
        const {
            divider: _divider,
            text: _text,
            icon: _icon,
            iconSize: _iconSize,
            iconMargin: _iconMargin,
            iconAlign: _iconAlign,
            ...dividerProps
        } = parsed
        return `<div data-gdi-divider="${escapeAttr(JSON.stringify(dividerProps))}"></div>`
    }

    // rangeMarker is standalone — outputs a block-level marker that the consuming
    // component (gdiTextBlock and the CMS design-system preview) hydrates into a
    // <GDIRangeMarker /> via a React portal.
    //
    // Syntax:
    //   {rangeMarker, labels: 'Rigid|Comfort|Stretch', value: 0}
    //   {rangeMarker, labels: 'Rigid|Comfort|Stretch', value: 0.7}
    //   {rangeMarker, labels: 'Tight|Regular|Relaxed', value: 1, my: '16px'}
    //   {rangeMarker, labels: 'Rigid|Comfort|Stretch', value: 1.5, color: 'webTypo.textPrimary'}
    if ('rangeMarker' in parsed) {
        const {
            rangeMarker: _rm,
            text: _text,
            icon: _icon,
            iconSize: _iconSize,
            iconMargin: _iconMargin,
            iconAlign: _iconAlign,
            labels: rawLabels,
            value: rawValue,
            ...restProps
        } = parsed
        // Split pipe-separated labels; trim each entry. Labels are decoded because
        // the CMS stores accented characters as HTML entities (e.g. "Serr&eacute;")
        // and they end up in a JSON attribute rather than in the rendered HTML.
        const labels = rawLabels
            ? decodeHtmlEntities(String(rawLabels))
                  .split('|')
                  .map((l) => l.trim())
                  .filter(Boolean)
            : []
        const value = rawValue != null ? Number(rawValue) : 0
        const cfg = {labels, value, ...restProps}
        return `<div data-gdi-range-marker="${escapeAttr(JSON.stringify(cfg))}"></div>`
    }

    // Step 1 — Apply fontStyle to the text only (not the icon).
    //
    // fontStyle accepts either a single token or a space-separated list of
    // tokens (e.g. "unset medium italic").
    //
    //   - Single token:
    //       * Semantic-tag styles (bold, italic, …) wrap the text in the
    //         corresponding semantic element so screen-readers benefit.
    //       * CSS-based styles (condensed, underline, unset, …) collect into
    //         an inline CSS string.
    //   - Multi-token list:
    //       * Every token is resolved to its CSS equivalent (tag-only tokens
    //         use FONT_STYLE_TAG_CSS_MAP) and concatenated in order. Later
    //         declarations override earlier ones, so authoring `unset` first
    //         resets all typography and the trailing tokens (e.g. `medium
    //         italic`) re-apply the desired weight/style on top.
    let styledText = escapedText
    const textOnlyStyles = []
    let fontStyleTextStyleToken = null

    if (fontStyle) {
        const fontStyleStr = String(fontStyle).trim()
        const tokens = fontStyleStr.split(/\s+/).filter(Boolean)

        if (tokens.length > 1) {
            // Multi-token mode — resolve every token to CSS so order wins.
            for (const token of tokens) {
                const css =
                    FONT_STYLE_CSS_MAP[token] ?? FONT_STYLE_TAG_CSS_MAP[token]
                if (css) textOnlyStyles.push(css)
            }
        } else {
            const single = tokens[0] ?? fontStyleStr
            const tagRenderer = FONT_STYLE_TAG_MAP[single]
            if (tagRenderer) {
                styledText = tagRenderer(escapedText)
            } else {
                const css = FONT_STYLE_CSS_MAP[single]
                if (css) textOnlyStyles.push(css)
                else if (/^[a-z][a-zA-Z0-9]*$/.test(single)) fontStyleTextStyleToken = single
            }
        }
    }

    // When an icon is present, isolate CSS fontStyle to a text-only span so it
    // does not bleed onto the icon element.
    const hasIcon = Boolean(icon)
    if (textOnlyStyles.length > 0 && hasIcon) {
        styledText = `<span style="${textOnlyStyles.join(';')}">${styledText}</span>`
    }

    // Step 2 — Build container-level styles (fontColor, highlight, and CSS fontStyle
    //           when there is no icon to isolate it from).
    const containerStyles = []

    if (textOnlyStyles.length > 0 && !hasIcon) {
        // No icon — merge fontStyle CSS into the container to avoid an extra span.
        containerStyles.push(...textOnlyStyles)
    }

    if (fontColor) {
        const color = resolveColor(fontColor)
        if (color) containerStyles.push(`color:${color}`)
    }

    if (lineHeight !== undefined && lineHeight !== null && lineHeight !== '') {
        containerStyles.push(`line-height:${String(lineHeight)}`)
    }

    const bgValue = highlight ?? backgroundColor
    if (bgValue) {
        const bg = resolveColor(String(bgValue))
        if (bg) {
            containerStyles.push(`background-color:${bg}`)
            // Highlight renders as an inline-block chip so it sits inline with
            // surrounding text when there's room, and wraps to its own line as a
            // single unit when the line runs out of space. inline-block also
            // makes vertical padding render reliably.
            containerStyles.push('display:inline-block')
            containerStyles.push('padding:3px 4px')
        }
    }

    // Icon + text: always use inline-flex so multi-line text wraps within its
    // own column (indented to the text start, not back under the icon).
    // Icon-only shortcodes (no text) skip the flex wrapper — the SVG's own
    // vertical-align:middle is sufficient and no outer container is needed.
    // iconAlign overrides the default center alignment.
    if (hasIcon && (iconAlign != null && iconAlign !== '' || styledText !== '')) {
        const flexAlign =
            iconAlign != null && iconAlign !== ''
                ? (ICON_ALIGN_TO_FLEX[String(iconAlign)] ?? 'center')
                : 'center'
        containerStyles.push(`display:inline-flex;vertical-align:middle;align-items:${flexAlign}`)
    }

    // Step 3 — Combine icon + styled text.
    let content = styledText
    if (hasIcon) {
        const iconHtml = iconRenderer(String(icon), {size: iconSize, margin: iconMargin, align: iconAlign})
        content = iconHtml + styledText
    }

    // Step 4 — Wrap in a container span when container styles are present or a
    // fontSize directive is specified. fontSize accepts either a design-system
    // textStyle token (e.g. webH1, bodySmRegular) — resolved to full Chakra
    // typography styles via data-gdi-textstyle by the host component — or a
    // direct CSS font-size value (e.g. 14px, 1.5rem); bare numbers are treated as px.
    let fontSizeToken = null
    if (fontSize != null && String(fontSize).trim() !== '') {
        const v = String(fontSize).trim()
        const cssUnitMatch = v.match(/^(\d+(?:\.\d+)?)(px|rem|em|vw|vh|ch|ex|%)$/)
        if (cssUnitMatch) {
            containerStyles.push(`font-size:${v}`)
        } else if (/^\d+(?:\.\d+)?$/.test(v)) {
            containerStyles.push(`font-size:${v}px`)
        } else {
            fontSizeToken = v
        }
    }
    const textStyleToken = fontSizeToken || fontStyleTextStyleToken
    const hasTextStyle = Boolean(textStyleToken)
    if (containerStyles.length > 0 || hasTextStyle) {
        const styleAttr = containerStyles.length > 0 ? ` style="${containerStyles.join(';')}"` : ''
        const tsAttr = hasTextStyle ? ` data-gdi-textstyle="${escapeAttr(textStyleToken)}"` : ''
        content = `<span${styleAttr}${tsAttr}>${content}</span>`
    }

    // Step 5 — Wrap in a link.
    if (link) {
        if (link.linkType === 'drawer' && link.drawerId) {
            // Drawer links: rendered as a plain <a> with data-gdi-drawer so the
            // gdiTextBlock React click handler can open the appropriate modal.
            const attrs = [
                `href="#"`,
                `class="gdi-drawer-link"`,
                `data-gdi-drawer="${escapeAttr(String(link.drawerId))}"`,
                `role="button"`
            ]
            if (link.ariaLabel) attrs.push(`aria-label="${escapeAttr(link.ariaLabel)}"`)
            content = `<a ${attrs.join(' ')}>${content}</a>`
        } else if (link.linkType === 'clara-chat') {
            // Clara-chat links: rendered as a plain <a> with data-gdi-clara-chat so
            // gdiTextBlock can attach a click handler that triggers the Clara button.
            const attrs = [
                `href="#"`,
                `class="gdi-clara-chat-link"`,
                `data-gdi-clara-chat="true"`,
                `role="button"`
            ]
            if (link.ariaLabel) attrs.push(`aria-label="${escapeAttr(link.ariaLabel)}"`)
            content = `<a ${attrs.join(' ')}>${content}</a>`
        } else {
            const href = resolveLinkHref(link, {
                categoryUrls: options.categoryUrls,
                buildUrl: options.buildUrl
            })
            if (href) {
                const attrs = [`href="${escapeAttr(href)}"`]
                if (link.newTab) attrs.push('target="_blank" rel="noopener noreferrer"')
                if (link.ariaLabel) attrs.push(`aria-label="${escapeAttr(link.ariaLabel)}"`)
                content = `<a ${attrs.join(' ')}>${content}</a>`
            }
        }
    }

    return content
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Process an HTML string and replace all embedded shortcode patterns with
 * their rendered HTML equivalents.
 *
 * Shortcodes that cannot be parsed are left unchanged in the output.
 *
 * @param {string} html    - Raw HTML string from the CMS (e.g. TextForWeb)
 * @param {Object} [options]
 * @param {Function} [options.iconRenderer] - Custom icon renderer (name → HTML string)
 * @param {function(string): string} [options.buildUrl] - Adds the site/locale prefix to internal link hrefs
 * @returns {string} Processed HTML string
 */
export const parseShortcodes = (html, options = {}) => {
    if (!html || typeof html !== 'string') return html

    const shortcodes = extractShortcodes(html)
    if (shortcodes.length === 0) return html

    let result = ''
    let lastIndex = 0

    for (const {raw, start, end} of shortcodes) {
        result += html.slice(lastIndex, start)
        try {
            const parsed = parseShortcode(raw)
            result += renderShortcode(parsed, options)
        } catch (_) {
            // Parsing failed — emit the raw shortcode unchanged
            result += raw
        }
        lastIndex = end + 1
    }

    result += html.slice(lastIndex)
    return result
}
